import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, FieldInputProps } from "formik";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  IconButton,
  Button,
  Text,
  Spinner,
  Switch,
  Image,
} from "@chakra-ui/react";
import {
  ViewIcon,
  ViewOffIcon,
  CloseIcon,
  AttachmentIcon,
} from "@chakra-ui/icons";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import CommonFormikInput from "@root/components/common/form/CommonFormikInput";
import CommonLabel from "@root/components/common/form/CommonLabel";
import useAdminHook from "@root/hooks/useAdminHook";
import { LoadingIcon } from "@root/assets/icons/LoadingIcon";

interface AddAdminProps {
  selectedRow?: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
}

export const AddAdmin: React.FC<AddAdminProps> = ({
  selectedRow,
  onCancel,
  onSubmit,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation("common");
  const { loader } = useAdminHook();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const certificateBorderImageInputRef = useRef<HTMLInputElement>(null);
  const stampInputRef = useRef<HTMLInputElement>(null);

  const AddAdminSchema = Yup.object().shape({
    name: Yup.string().required(t("superAdminDashboard.adminNameRequired")),
    ...(!selectedRow && {
      email: Yup.string()
        .email(t("superAdminDashboard.invalidEmail"))
        .required(t("superAdminDashboard.emailRequired")),
      certificateOrgName: Yup.string().required(
        t("superAdminDashboard.certificateOrgNameRequired")
      ),
      certificateOrgNameKr: Yup.string().required(
        t("superAdminDashboard.certificateOrgNameKrRequired")
      ),
      domain: Yup.string().required(t("superAdminDashboard.domainRequired")),
      logo: Yup.mixed().required(t("superAdminDashboard.logoRequired")),
      favicon: Yup.mixed().required(t("superAdminDashboard.faviconRequired")),
      certificateBorderImage: Yup.mixed().required(
        t("superAdminDashboard.certificateBorderImageRequired")
      ),
      stamp: Yup.mixed().required(t("superAdminDashboard.stampRequired")),
    }),
    ...(selectedRow && {
      certificateOrgName: Yup.string().optional(),
      certificateOrgNameKr: Yup.string().optional(),
      domain: Yup.string().optional(),
    }),
  });

  const formikRef = useRef<any>(null);

  useEffect(() => {
    if (selectedRow && formikRef.current) {
      formikRef.current.setFieldValue("name", selectedRow.name || "");
      formikRef.current.setFieldValue("email", selectedRow.email || "N/A");
      formikRef.current.setFieldValue("id", selectedRow.id || "");
      formikRef.current.setFieldValue(
        "certificateOrgName",
        selectedRow.certificateOrgName || ""
      );
      formikRef.current.setFieldValue(
        "certificateOrgNameKr",
        selectedRow.certificateOrgNameKr || ""
      );
      formikRef.current.setFieldValue("domain", selectedRow.domain || "");
      formikRef.current.setFieldValue("logo", selectedRow.logo || null);
      formikRef.current.setFieldValue("favicon", selectedRow.favicon || null);
      formikRef.current.setFieldValue(
        "certificateBorderImage",
        selectedRow.certificateBorderImage || null
      );
      formikRef.current.setFieldValue("stamp", selectedRow.stamp || null);
      formikRef.current.setFieldValue("browserTabName", selectedRow.browserTabName || "");
      formikRef.current.setFieldValue("chairmanNameKr", selectedRow.chairmanNameKr || "");
      formikRef.current.setFieldValue("chairmanNameEn", selectedRow.chairmanNameEn || "");
    }
  }, [selectedRow]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setFieldValue(fieldName, file);
    }
  };

  return (
    <Box bg="white" borderRadius="2xl" p={6}>
      {/* Header with Title and Close Button */}
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={8}
        pb={4}
        borderBottom="1px solid #E4EBF0"
      >
        <Text
          fontSize="24px"
          fontWeight={600}
          fontFamily="Poppins"
          color="#2F2F34"
        >
          {selectedRow
            ? t("superAdminDashboard.updateAnAdmin")
            : t("superAdminDashboard.addAnAdmin")}
        </Text>
      </Flex>

      <Formik
        innerRef={formikRef}
        initialValues={{
          name: selectedRow?.name || "",
          email: selectedRow?.email || "",
          id: selectedRow?.id || "",
          logo: selectedRow?.logo || null,
          favicon: selectedRow?.favicon || null,
          certificateBorderImage: selectedRow?.certificateBorderImage || null,
          stamp: selectedRow?.stamp || null,
          certificateOrgName: selectedRow?.certificateOrgName || "",
          certificateOrgNameKr: selectedRow?.certificateOrgNameKr || "",
          domain: selectedRow?.domain || "",
          browserTabName: selectedRow?.browserTabName || "",
          chairmanNameKr: selectedRow?.chairmanNameKr || "",
          chairmanNameEn: selectedRow?.chairmanNameEn || "",
        }}
        enableReinitialize
        validationSchema={AddAdminSchema}
        onSubmit={
          onSubmit || ((values) => alert(JSON.stringify(values, null, 2)))
        }
      >
        {({ errors, touched, isSubmitting, setFieldValue, values }) => (
          <Form>
            <Box mb={6}>
              <CommonFormikInput
                name="name"
                label={`${t("superAdminDashboard.adminName")} *`}
                placeholder={t("superAdminDashboard.adminNamePlaceholder")}
              />
            </Box>
            <Box mb={6}>
              <CommonFormikInput
                disabled={selectedRow}
                name="email"
                label={`${t("superAdminDashboard.email")}${!selectedRow ? " *" : ""}`}
                placeholder={t("superAdminDashboard.emailPlaceholder")}
              />
            </Box>

            {/* Certificate Org Name */}
            <Box mb={6}>
              <CommonFormikInput
                name="certificateOrgName"
                label={`${t("superAdminDashboard.certificateOrgName")}${!selectedRow ? " *" : ""}`}
                placeholder={t(
                  "superAdminDashboard.certificateOrgNamePlaceholder"
                )}
              />
            </Box>

            {/* Certificate Org Name KR */}
            <Box mb={6}>
              <CommonFormikInput
                name="certificateOrgNameKr"
                label={`${t("superAdminDashboard.certificateOrgNameKr")}${!selectedRow ? " *" : ""}`}
                placeholder={t(
                  "superAdminDashboard.certificateOrgNameKrPlaceholder"
                )}
              />
            </Box>

            {/* Domain */}
            <Box mb={6}>
              <CommonFormikInput
                name="domain"
                label={`${t("superAdminDashboard.domain")}${!selectedRow ? " *" : ""}`}
                placeholder={t("superAdminDashboard.domainPlaceholder")}
              />
            </Box>

            {/* Browser Tab Name */}
            <Box mb={6}>
              <CommonFormikInput
                name="browserTabName"
                label={t("superAdminDashboard.browserTabName", "Browser Tab Name")}
                placeholder={t("superAdminDashboard.browserTabNamePlaceholder", "Enter browser tab name")}
              />
            </Box>

            {/* Chairman Name (KR) */}
            <Box mb={6}>
              <CommonFormikInput
                name="chairmanNameKr"
                label={t("superAdminDashboard.chairmanNameKr", "Chairman Name (KR)")}
                placeholder={t("superAdminDashboard.chairmanNameKrPlaceholder", "Enter chairman name (KR)")}
              />
            </Box>

            {/* Chairman Name (EN) */}
            <Box mb={6}>
              <CommonFormikInput
                name="chairmanNameEn"
                label={t("superAdminDashboard.chairmanNameEn", "Chairman Name (EN)")}
                placeholder={t("superAdminDashboard.chairmanNameEnPlaceholder", "Enter chairman name (EN)")}
              />
            </Box>

            {/* Logo Upload */}
            <Box mb={6}>
              <CommonLabel mb={2}>
                {t("superAdminDashboard.logo")}
                {!selectedRow && (
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                )}
              </CommonLabel>
              <input
                type="file"
                accept="image/*,.pdf,application/pdf,*/*"
                ref={logoInputRef}
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, "logo", setFieldValue)}
              />
              <Flex alignItems="center" gap={4}>
                <Button
                  leftIcon={<AttachmentIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                >
                  {t("superAdminDashboard.uploadLogo")}
                </Button>
                {values.logo && (
                  <Text fontSize="sm" color="gray.600">
                    {typeof values.logo === "string"
                      ? t("superAdminDashboard.existingLogo")
                      : values.logo.name}
                  </Text>
                )}
                {values.logo && typeof values.logo === "string" && (
                  <Image
                    src={values.logo}
                    alt={t("superAdminDashboard.logo")}
                    boxSize="40px"
                    objectFit="contain"
                  />
                )}
              </Flex>
              {!selectedRow && errors.logo && (
                <FormErrorMessage mt={1}>
                  {String(errors.logo)}
                </FormErrorMessage>
              )}
            </Box>

            {/* Favicon Upload */}
            <Box mb={6}>
              <CommonLabel mb={2}>
                {t("superAdminDashboard.favicon")}
                {!selectedRow && (
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                )}
              </CommonLabel>
              <input
                type="file"
                accept="image/*,.pdf,application/pdf,*/*"
                ref={faviconInputRef}
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, "favicon", setFieldValue)}
              />
              <Flex alignItems="center" gap={4}>
                <Button
                  leftIcon={<AttachmentIcon />}
                  onClick={() => faviconInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                >
                  {t("superAdminDashboard.uploadFavicon")}
                </Button>
                {values.favicon && (
                  <Text fontSize="sm" color="gray.600">
                    {typeof values.favicon === "string"
                      ? t("superAdminDashboard.existingFavicon")
                      : values.favicon.name}
                  </Text>
                )}
                {values.favicon && typeof values.favicon === "string" && (
                  <Image
                    src={values.favicon}
                    alt={t("superAdminDashboard.favicon")}
                    boxSize="40px"
                    objectFit="contain"
                  />
                )}
              </Flex>
              {!selectedRow && errors.favicon && (
                <FormErrorMessage mt={1}>
                  {String(errors.favicon)}
                </FormErrorMessage>
              )}
            </Box>

            {/* Certificate Border Image Upload */}
            <Box mb={6}>
              <CommonLabel mb={2}>
                {t("superAdminDashboard.certificateBorderImage")}
                {!selectedRow && (
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                )}
              </CommonLabel>
              <input
                type="file"
                accept="image/*,.pdf,application/pdf,*/*"
                ref={certificateBorderImageInputRef}
                style={{ display: "none" }}
                onChange={(e) =>
                  handleFileChange(e, "certificateBorderImage", setFieldValue)
                }
              />
              <Flex alignItems="center" gap={4}>
                <Button
                  leftIcon={<AttachmentIcon />}
                  onClick={() =>
                    certificateBorderImageInputRef.current?.click()
                  }
                  variant="outline"
                  size="sm"
                >
                  {t("superAdminDashboard.uploadCertificateBorderImage")}
                </Button>
                {values.certificateBorderImage && (
                  <Text fontSize="sm" color="gray.600">
                    {typeof values.certificateBorderImage === "string"
                      ? t("superAdminDashboard.existingCertificateBorderImage")
                      : values.certificateBorderImage.name}
                  </Text>
                )}
                {values.certificateBorderImage &&
                  typeof values.certificateBorderImage === "string" && (
                    <Image
                      src={values.certificateBorderImage}
                      alt={t("superAdminDashboard.certificateBorderImage")}
                      boxSize="40px"
                      objectFit="contain"
                    />
                  )}
              </Flex>
              {!selectedRow && errors.certificateBorderImage && (
                <FormErrorMessage mt={1}>
                  {String(errors.certificateBorderImage)}
                </FormErrorMessage>
              )}
            </Box>

            {/* Stamp Upload */}
            <Box mb={8}>
              <CommonLabel mb={2}>
                {t("superAdminDashboard.stamp")}
                {!selectedRow && (
                  <Text as="span" color="red.500" ml={1}>
                    *
                  </Text>
                )}
              </CommonLabel>
              <input
                type="file"
                accept="image/*,.pdf,application/pdf,*/*"
                ref={stampInputRef}
                style={{ display: "none" }}
                onChange={(e) => handleFileChange(e, "stamp", setFieldValue)}
              />
              <Flex alignItems="center" gap={4}>
                <Button
                  leftIcon={<AttachmentIcon />}
                  onClick={() => stampInputRef.current?.click()}
                  variant="outline"
                  size="sm"
                >
                  {t("superAdminDashboard.uploadStamp")}
                </Button>
                {values.stamp && (
                  <Text fontSize="sm" color="gray.600">
                    {typeof values.stamp === "string"
                      ? t("superAdminDashboard.existingStamp")
                      : values.stamp.name}
                  </Text>
                )}
                {values.stamp && typeof values.stamp === "string" && (
                  <Image
                    src={values.stamp}
                    alt={t("superAdminDashboard.stamp")}
                    boxSize="40px"
                    objectFit="contain"
                  />
                )}
              </Flex>
              {!selectedRow && errors.stamp && (
                <FormErrorMessage mt={1}>
                  {String(errors.stamp)}
                </FormErrorMessage>
              )}
            </Box>

            <Flex justify="flex-end" mt={24} gap={4}>
              <Button
                variant="outline"
                color="#1819B4"
                borderColor="#1819B4"
                borderRadius="8px"
                fontWeight={500}
                width="120px"
                height="48px"
                onClick={onCancel}
                type="button"
                _hover={{ bg: "#F3F4F6" }}
              >
                {t("superAdminDashboard.cancel")}
              </Button>
              <Button
                colorScheme="blue"
                bg="#1819B4"
                color="white"
                borderRadius="8px"
                fontWeight={500}
                width="140px"
                height="48px"
                type="submit"
                disabled={isSubmitting || loader}
                _hover={{ bg: "#1415A0" }}
              >
                {isSubmitting || loader ? (
                  <Spinner size="sm" color={"white"} />
                ) : selectedRow ? (
                  t("superAdminDashboard.updateAdmin")
                ) : (
                  t("superAdminDashboard.addAdmin")
                )}
              </Button>
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
